// The identity-mapping CSV import + template download, as a hook.
//
// It used to be one component (FD-099) rendering two buttons above the mapping
// table. The campaign header's `···` menu now carries those two actions, and a
// Fluent `MenuList` UNMOUNTS ITS CONTENTS WHEN THE MENU CLOSES — so a component
// that owned the hidden `<input type="file">` and the result MessageBars could
// never live inside it: clicking "Import CSV" would close the menu and tear the
// input down before the picker could return.
//
// So the state and the two handlers live here, and a caller renders the
// triggers wherever it likes while rendering `fileInput` and `feedback`
// somewhere that stays mounted. `IdentityMappingCsvControls` is the button
// variant (still used by `Resources › Identities`, which has no `···` menu);
// `MigrationLayout` is the menu variant.

import { ChangeEvent, ReactNode, useCallback, useRef, useState } from 'react';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  makeStyles,
  tokens,
} from '@fluentui/react-components';

import { ApiError } from '../../api/client';
import {
  extractConsentError,
  getIdentityMappingTemplate,
  importIdentityMappings,
} from '../../api/identity';
import { ConsentErrorData, SetIdentityMappingsData } from '../../types/identity';
import ConsentMessageBar from './ConsentMessageBar';

const useStyles = makeStyles({
  feedback: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalS,
  },
  hiddenInput: { display: 'none' },
  rejectList: { margin: 0, paddingLeft: tokens.spacingHorizontalL },
});

function formatError(e: unknown): string {
  if (e instanceof ApiError && e.body && typeof e.body === 'object') {
    const msg = (e.body as { error?: { message?: string } }).error?.message;
    if (msg) return msg;
  }
  return e instanceof Error ? e.message : 'CSV import failed';
}

export interface UseIdentityCsvOptions {
  migrationId: string;
  /** Invoked after an import that wrote at least one row. */
  onImported: () => void;
}

export interface IdentityCsvControls {
  busy: 'download' | 'import' | null;
  downloadTemplate: () => void;
  openFilePicker: () => void;
  /** MUST be rendered by the caller, and never inside a Menu. */
  fileInput: ReactNode;
  /** Consent / error / bucket-summary bars. `null` when there is nothing to say. */
  feedback: ReactNode;
}

export function useIdentityCsv({
  migrationId,
  onImported,
}: UseIdentityCsvOptions): IdentityCsvControls {
  const styles = useStyles();
  const fileRef = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState<'download' | 'import' | null>(null);
  const [result, setResult] = useState<{
    status: number;
    data: SetIdentityMappingsData;
  } | null>(null);
  const [consent, setConsent] = useState<ConsentErrorData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const downloadTemplate = useCallback(async () => {
    setBusy('download');
    setError(null);
    try {
      const template = await getIdentityMappingTemplate(migrationId);
      const blob = new Blob([template.csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = template.filename;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(null);
    }
  }, [migrationId]);

  const openFilePicker = useCallback(() => fileRef.current?.click(), []);

  const handleFile = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      // Reset the input so selecting the same file again re-triggers change.
      if (fileRef.current) fileRef.current.value = '';
      if (!file) return;

      setBusy('import');
      setError(null);
      setConsent(null);
      setResult(null);
      try {
        const csv = await file.text();
        const res = await importIdentityMappings(migrationId, csv);
        setResult({ status: res.status, data: res.data });
        if (res.data.accepted.length + res.data.updated.length > 0) onImported();
      } catch (e) {
        const consentErr = extractConsentError(e);
        if (consentErr) setConsent(consentErr);
        else setError(formatError(e));
      } finally {
        setBusy(null);
      }
    },
    [migrationId, onImported],
  );

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      accept=".csv,text/csv"
      className={styles.hiddenInput}
      onChange={handleFile}
    />
  );

  const hasFeedback = consent !== null || error !== null || result !== null;
  const feedback = hasFeedback ? (
    <div className={styles.feedback}>
      {consent && <ConsentMessageBar consent={consent} />}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>
            <MessageBarTitle>CSV import failed</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}
      {result && (
        <MessageBar intent={result.data.rejected.length > 0 ? 'warning' : 'success'}>
          <MessageBarBody>
            <MessageBarTitle>
              {result.data.accepted.length} added, {result.data.updated.length} updated,{' '}
              {result.data.rejected.length} rejected
            </MessageBarTitle>
            {result.data.rejected.length > 0 && (
              <ul className={styles.rejectList}>
                {result.data.rejected.map((r, i) => (
                  <li key={i}>
                    {(r.sourceUserId ?? r.sourceUserSid ?? '(row)') + ': ' + r.reason}
                  </li>
                ))}
              </ul>
            )}
          </MessageBarBody>
        </MessageBar>
      )}
    </div>
  ) : null;

  return { busy, downloadTemplate, openFilePicker, fileInput, feedback };
}
